import React, { useEffect, useState, useCallback } from "react";
import { formatBRL, formatDateTimeBR } from "../lib/api";
import { fetchWithV2Fallback } from "../lib/v2";
import { FileText, RefreshCw, AlertCircle, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import StatusBadge from "../components/StatusBadge";

const TITLE_STATUS_MAP = {
  WAITING_PAYMENT: "pendente",
  PARTIALLY_PAID: "aprovada",
  PAID: "liquidada",
  OVERDUE: "vencida",
  WRITTEN_OFF: "cancelada",
  CANCELLED: "cancelada",
};

export default function Receivables() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("");
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [balanceEvents, setBalanceEvents] = useState({});
  const [loadingEvents, setLoadingEvents] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWithV2Fallback("/v2/titles", "/v2/receivables");
      const list = result.data?.items || result.data?.receivables || (Array.isArray(result.data) ? result.data : []);
      setItems(list);
      setSource(result.source);
    } catch {
      setError("Erro ao carregar titulos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = async (titleId) => {
    if (expandedId === titleId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(titleId);
    if (!balanceEvents[titleId]) {
      setLoadingEvents(titleId);
      try {
        const result = await fetchWithV2Fallback(`/v2/titles/${titleId}/balance-events`, null);
        const events = result.data?.events || (Array.isArray(result.data) ? result.data : []);
        setBalanceEvents(prev => ({ ...prev, [titleId]: events }));
      } catch {
        setBalanceEvents(prev => ({ ...prev, [titleId]: [] }));
      } finally {
        setLoadingEvents(null);
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="receivables-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <span className="zemo-overline">Operacoes</span>
          <h1 className="mt-1.5 text-3xl sm:text-[34px] font-semibold tracking-tight text-zinc-900">Titulos & Recebiveis</h1>
          <p className="mt-1 text-sm text-zinc-500">Recebiveis individuais com saldo devedor rastreado automaticamente.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="receivables-refresh">
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm" data-testid="receivables-error">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}

      {loading ? (
        <Skeleton className="h-64 rounded-lg" />
      ) : items.length === 0 ? (
        <div className="zemo-card p-12 text-center" data-testid="receivables-empty">
          <FileText className="h-10 w-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500">Nenhum titulo disponivel</p>
          <p className="text-xs text-zinc-400 mt-1">Titulos aparecerao quando operacoes forem criadas na V2.</p>
        </div>
      ) : (
        <div className="zemo-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500 border-b border-[var(--zemo-border)]">
                <tr>
                  <th className="w-8 px-3 py-3"></th>
                  <th className="text-left px-4 py-3 font-semibold">ID Externo</th>
                  <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                  <th className="text-right px-4 py-3 font-semibold">Valor Face</th>
                  <th className="text-right px-4 py-3 font-semibold">Valor Liquido</th>
                  <th className="text-right px-4 py-3 font-semibold">Saldo Devedor</th>
                  <th className="text-left px-4 py-3 font-semibold">Vencimento</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--zemo-border)]" data-testid="receivables-table">
                {items.map((r) => {
                  const isExpanded = expandedId === r.id;
                  const portalStatus = TITLE_STATUS_MAP[r.status] || (r.status || "pendente").toLowerCase();
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="hover:bg-zinc-50/60 cursor-pointer" onClick={() => r.id && toggleExpand(r.id)}>
                        <td className="px-3 py-3 text-zinc-400">
                          {r.id ? (isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />) : null}
                        </td>
                        <td className="px-4 py-3.5 font-mono-num font-semibold text-zinc-900">{r.external_id || r.numero || "--"}</td>
                        <td className="px-4 py-3.5 text-zinc-600 text-xs">{r.backing_type || "--"}</td>
                        {/* Vocabulario canonico da V2 (PUBLIC_ALIASES) na FRENTE da cadeia legada.
                            Seleção por `??` (nullish), NUNCA por `||`: um `0` canonico legitimo nao
                            pode ser descartado. A cadeia legada sobrevive intacta (migracao
                            incremental — a V2 1.x ecoa os dois nomes; na 2.0 so o canonico chega). */}
                        <td className="px-4 py-3.5 text-right font-mono-num">{formatBRL(parseFloat(r.gross_future_value ?? (r.gross_face_value || r.face_value || r.valor || 0)))}</td>
                        <td className="px-4 py-3.5 text-right font-mono-num">{formatBRL(parseFloat(r.net_present_liquid_value ?? (r.liquid_value || r.valor_liquido || 0)))}</td>
                        <td className="px-4 py-3.5 text-right font-mono-num font-semibold">
                          {r.current_outstanding_balance_brl
                            ? formatBRL(parseFloat(r.current_outstanding_balance_brl))
                            : "--"}
                        </td>
                        <td className="px-4 py-3.5 text-[12px] text-zinc-500">{r.due_date || formatDateTimeBR(r.vencimento) || "--"}</td>
                        <td className="px-4 py-3.5"><StatusBadge status={portalStatus} /></td>
                      </tr>
                      {/* Balance events expandable row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={8} className="bg-zinc-50/80 px-8 py-4">
                            <div className="text-xs font-semibold text-zinc-600 mb-2">Historico de eventos do saldo</div>
                            {loadingEvents === r.id ? (
                              <Skeleton className="h-16 rounded" />
                            ) : (balanceEvents[r.id] || []).length === 0 ? (
                              <p className="text-xs text-zinc-400">Nenhum evento registrado ou V2 indisponivel.</p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-zinc-500 border-b border-zinc-200">
                                    <th className="text-left py-1.5 pr-4">Evento</th>
                                    <th className="text-right py-1.5 pr-4">Valor</th>
                                    <th className="text-right py-1.5 pr-4">Saldo apos</th>
                                    <th className="text-left py-1.5">Data</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(balanceEvents[r.id] || []).map((evt, i) => (
                                    <tr key={i} className="border-b border-zinc-100 last:border-0">
                                      <td className="py-1.5 pr-4 font-medium text-zinc-700">{evt.event_type}</td>
                                      <td className="py-1.5 pr-4 text-right font-mono-num">{evt.payment_delta_brl ? formatBRL(parseFloat(evt.payment_delta_brl)) : "--"}</td>
                                      <td className="py-1.5 pr-4 text-right font-mono-num font-semibold">{evt.outstanding_after_brl ? formatBRL(parseFloat(evt.outstanding_after_brl)) : "--"}</td>
                                      <td className="py-1.5 text-zinc-500">{evt.effective_at || "--"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {source && (
        <div className="text-[11px] text-zinc-400 text-right">Fonte: {source === "v2" ? "API V2" : "Local"}</div>
      )}
    </div>
  );
}
